import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, IconButton, Button, Menu, CircularProgress } from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  DragIndicator as DragIndicatorIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DashboardGrid, { DashboardItem } from './components/DashboardGrid';
import WidgetPicker, { WidgetTemplate } from './components/WidgetPicker';
import DashboardTour from './components/DashboardTour';
import DashboardWidgetViewport from './components/DashboardWidgetViewport';
import widgetComponents from './components/widgets';
import { meApi } from '../../api';
import { PRODUCT } from '../../branding';
import {
  DashboardLayoutItem,
  deriveVisibleDashboardState,
  getDefaultDashboardState,
  getWidgetTitleKey,
  loadDashboardState,
  mergeVisibleLayout,
  resolveInitialDashboardState,
  saveDashboardState,
} from './dashboardConfig';
import { getCurrentOrgId } from '../../api/client';
import {
  WIDGET_GUTTER,
  WIDGET_HANDLE_ICON,
  WIDGET_HANDLE_INSET,
  WIDGET_HANDLE_OPTICAL_RISE,
  WIDGET_HANDLE_PAD,
} from '../../theme/shellMetrics';

// Generate unique ID
const generateId = () => Math.random().toString(36).substr(2, 9);

// The layout is stored PER ORGANIZATION — see `loadDashboardState`. It used to
// live under one global key, which gave an admin with several orgs a single
// shared layout; each org licenses different products, so the widgets the new org
// lacked were filtered out and the grid reflowed into the holes. That is the
// "widgets drift after switching org" P0.

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isWidgetPickerOpen, setIsWidgetPickerOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
  const [licensedProducts, setLicensedProducts] = useState<string[] | null>(null);
  const [items, setItems] = useState<DashboardItem[]>([]);
  const [layout, setLayout] = useState<DashboardLayoutItem[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const initialize = async () => {
      let products: string[];
      try {
        const dashboard = await meApi.getDashboard();
        products = Array.isArray(dashboard?.licensed_products) ? dashboard.licensed_products : [];
      } catch {
        products = [PRODUCT];
      }
      if (cancelled) return;

      const orgId = getCurrentOrgId();
      const saved = loadDashboardState(orgId);
      const initial = saved
        ? resolveInitialDashboardState(JSON.stringify(saved), products)
        : resolveInitialDashboardState(null, products);
      setLicensedProducts(products);
      setItems(initial.items);
      setLayout(initial.layout);
      setReady(true);
    };
    void initialize();
    return () => { cancelled = true; };
  }, []);

  const visibleState = useMemo(
    () => deriveVisibleDashboardState({ items, layout }, licensedProducts ?? []),
    [items, layout, licensedProducts],
  );
  const visibleItemIds = useMemo(
    () => new Set(visibleState.items.map((item) => item.id)),
    [visibleState.items],
  );

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (!ready) return;
    // Scoped to the org that is current RIGHT NOW rather than one captured at
    // mount: switching org triggers a full reload (OrgSwitcher), so the two are
    // the same today, and reading it here means they cannot diverge if that ever
    // becomes a soft switch.
    saveDashboardState(getCurrentOrgId(), { items, layout });
  }, [items, layout, ready]);

  // Handle adding new widget
  const handleAddWidget = useCallback((template: WidgetTemplate) => {
    const newId = generateId();
    const newItem: DashboardItem = {
      id: newId,
      type: template.type,
      i18nKey: template.i18nKey,
    };

    setItems(prev => [...prev, newItem]);
    setLayout(prev => [
      ...prev,
      {
        i: newId,
        x: 0,
        y: prev.reduce((bottom, entry) => Math.max(bottom, entry.y + entry.h), 0),
        w: template.defaultSize.w,
        h: template.defaultSize.h,
        minW: 2,
        minH: 3,
      },
    ]);
  }, []);

  // Handle widget menu open
  const handleWidgetMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, itemId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(prev => ({
      ...prev,
      [itemId]: event.currentTarget
    }));
  }, []);

  // Handle widget menu close
  const handleWidgetMenuClose = useCallback((itemId: string) => {
    setMenuAnchorEl(prev => ({
      ...prev,
      [itemId]: null
    }));
  }, []);

  // Handle removing widget
  const handleRemoveWidget = useCallback((itemId: string) => {
    handleWidgetMenuClose(itemId);
    setItems(prev => prev.filter(item => item.id !== itemId));
    setLayout(prev => prev.filter(item => item.i !== itemId));
  }, [handleWidgetMenuClose]);

  // Handle layout change
  const handleLayoutChange = useCallback((newLayout: DashboardLayoutItem[]) => {
    setLayout((current) => mergeVisibleLayout(current, newLayout, visibleItemIds));
  }, [visibleItemIds]);

  // Reset layout to default
  const handleResetLayout = useCallback(() => {
    if (!licensedProducts) return;
    const defaults = getDefaultDashboardState(licensedProducts);
    setItems(defaults.items);
    setLayout(defaults.layout);
  }, [licensedProducts]);

  // Render widget content
  const renderWidget = useCallback((item: DashboardItem) => {
    const WidgetComponent = widgetComponents[item.type];
    const isMenuOpen = Boolean(menuAnchorEl[item.id]);
    const isFirst = visibleState.items.length > 0 && item.id === visibleState.items[0].id;

    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.2)',
          boxShadow: 'none',
        }}
      >
        {/* Widget Title Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            // ONE horizontal inset for the whole card — this bar and the body below both read
            // `WIDGET_GUTTER` and nothing inside a widget may add another. Alignment is INHERITED,
            // not arithmetic: the previous attempt summed the handle's flow width into the title's
            // offset, which only holds while every widget agrees on the sum, and fourteen of
            // nineteen did not.
            //
            // The handle is FIRST IN THE FLOW here, as a flex sibling of the title, so the single
            // `alignItems: 'center'` above centres BOTH. It was absolutely positioned before, which
            // centred it on this bar's padding box while the title centred on its content box — two
            // rules for one row, and they drifted apart visibly.
            //
            // So the bar's own left padding is only `WIDGET_HANDLE_INSET`; the handle then occupies
            // the rest of the gutter, and the title begins exactly at `WIDGET_GUTTER` — which is what
            // the body uses. The equality is asserted, not assumed.
            position: 'relative',
            pl: WIDGET_HANDLE_INSET,
            pr: WIDGET_GUTTER,
            py: 1.5,
            bgcolor: '#ffffff',
          }}
        >
          {/* Drag handle — TOP-LEFT, where it has always been.
             *
             * `position: absolute`, so it does NOT push the title: that is the whole point. The bar
             * is `position: relative` and `WIDGET_GUTTER` is wide enough to clear the icon, so the
             * title starts on the body's axis with the control still in the corner the owner reaches
             * for. A previous pass moved it to the right cluster to win the same alignment, which was
             * not asked for and broke dragging.
             *
             * THE INNER `<Box>` IS LOAD-BEARING. `DashboardGrid` drags on
             * `draggableHandle=".widget-drag-handle > div"` — a DIV CHILD. Removing it leaves the
             * class in place and the whole dashboard immovable, which is exactly what happened: the
             * class was present and the drag was dead, so a check counting the class saw nothing
             * wrong (svdy-ui §16 rule 5 — presence is not function). `widgetContentAxis.test.ts` now
             * derives that selector from the grid and asserts the structure it needs.
             */}
          <Box
            className="widget-drag-handle"
            sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <Box
              {...(isFirst ? { 'data-tour': 'drag-handle' } : {})}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'move',
                p: WIDGET_HANDLE_PAD,
                // Optical, not geometric — see the token's derivation. The boxes are already centred
                // by the bar's single rule; this moves the ICON's ink onto the title's ink centre,
                // which a screenshot measurement showed sat 1px higher.
                mt: `-${WIDGET_HANDLE_OPTICAL_RISE}px`,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: WIDGET_HANDLE_ICON, color: 'text.secondary' }} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: '0.9rem',
                color: 'text.primary',
                letterSpacing: '0.01em',
              }}
            >
              {i18n.exists(getWidgetTitleKey(item.type))
                ? t(getWidgetTitleKey(item.type))
                : (item.title ?? item.type)}
            </Typography>
          </Box>

          {/* Actions Area */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <IconButton
              {...(isFirst ? { 'data-tour': 'widget-menu' } : {})}
              size="small"
              onClick={(e) => handleWidgetMenuOpen(e, item.id)}
              sx={{
                ml: 1,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl[item.id]}
              open={isMenuOpen}
              onClose={() => handleWidgetMenuClose(item.id)}
              onClick={(e) => e.stopPropagation()}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  overflow: 'visible',
                  mt: 0.5
                }
              }}
              sx={{
                '& .MuiList-root': { 
                  p: 0,
                  backgroundColor: 'transparent'
                }
              }}
            >
              <Button 
                onClick={() => handleRemoveWidget(item.id)}
                variant="outlined"
                size="small"
                sx={{ 
                  borderRadius: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  color: 'error.main',
                  border: '1px solid',
                  borderColor: 'error.main',
                  bgcolor: '#ffffff',
                  height: '32px',
                  px: 2,
                  '&:hover': {
                    borderColor: 'error.dark',
                    bgcolor: '#ffffff',
                  },
                }}
              >
                {t('dashboard:removeWidget')}
              </Button>
            </Menu>
          </Box>
        </Box>

        {/* Widget Content Area */}
        <Box sx={{
          flex: 1,
          // The SAME token as the title bar. Vertical padding differs; the horizontal inset cannot.
          px: WIDGET_GUTTER,
          pb: 2,
          overflow: 'auto',
          bgcolor: '#ffffff',
        }}>
          {WidgetComponent ? (
            <DashboardWidgetViewport>
              <WidgetComponent licensedProducts={licensedProducts ?? []} />
            </DashboardWidgetViewport>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('dashboard:widgetNotImplemented', { type: item.type })}
            </Typography>
          )}
        </Box>
      </Paper>
    );
  }, [handleWidgetMenuOpen, handleWidgetMenuClose, handleRemoveWidget, menuAnchorEl, visibleState.items, licensedProducts, i18n, t]);

  if (!ready || !licensedProducts) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      bgcolor: 'grey.50',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Main Dashboard Content */}
      <Box sx={{
        flex: 1,
        transition: theme => theme.transitions.create(['width', 'margin'], {
          duration: theme.transitions.duration.standard,
          easing: theme.transitions.easing.easeInOut,
        }),
        width: '100%',
        marginInlineEnd: isWidgetPickerOpen ? '360px' : 0
      }}>
        {/* Top Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary'
            }}
          >
            {t('dashboard:title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleResetLayout}
              size="small"
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.2)',
                bgcolor: '#ffffff',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.35)',
                  bgcolor: '#ffffff',
                }
              }}
            >
              {t('dashboard:resetLayout')}
            </Button>
            <Button
              data-tour="add-widgets"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setIsWidgetPickerOpen(true)}
              size="small"
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main',
                bgcolor: '#ffffff',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: '#ffffff',
                }
              }}
            >
              {t('dashboard:addWidgets')}
            </Button>
          </Box>
        </Box>

        {/* Dashboard Grid */}
        <Box
          sx={{
            pb: 1.5,
            minHeight: 'calc(100vh - 120px)'
          }}
        >
          <DashboardGrid
            layout={visibleState.layout}
            items={visibleState.items}
            onLayoutChange={handleLayoutChange}
            renderWidget={renderWidget}
          />
        </Box>
      </Box>

      {/* Widget Picker */}
      <Box
        sx={{
          width: 360,
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.2)',
          borderTop: 0,
          borderBottom: 0,
          borderInlineEnd: 0,
          bgcolor: 'background.paper',
          height: 'calc(100vh - 64px)', // Adjust for top nav height
          position: 'fixed',
          insetInlineEnd: isWidgetPickerOpen ? 0 : -360,
          top: 64,  // Top navigation height
          transition: theme => theme.transitions.create('right', {
            duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut,
          }),
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
          zIndex: theme => theme.zIndex.drawer,
          overflowY: 'hidden'
        }}
      >
        <WidgetPicker
          open={isWidgetPickerOpen}
          onClose={() => setIsWidgetPickerOpen(false)}
          onSelectWidget={handleAddWidget}
          activeTypes={items.map(i => i.type)}
          licensedProducts={licensedProducts}
        />
      </Box>

      <DashboardTour />
    </Box>
  );
};

export default Dashboard;